/**
 * Utility function to print invoices with support for A4 and Thermal formats
 * @param {Object} order - The order object to print
 * @param {string} printSize - 'a4' or 'thermal'
 */
export const printInvoice = (order, printSize = 'a4') => {
  if (!order) return;

  const isThermal = printSize === 'thermal';
  const printWindow = window.open('', '_blank');
  
  // Thermal print styling (58mm or 80mm thermal printer)
  const thermalStyles = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: "Segoe UI", Arial, sans-serif; 
      padding: 5px; 
      width: 80mm;
      font-size: 11px;
      color: #000;
    }
    .header { 
      text-align: center; 
      margin-bottom: 10px; 
      border-bottom: 1px dashed #000;
      padding-bottom: 5px;
    }
    .header h1 { 
      margin: 0; 
      font-size: 18px; 
      font-weight: bold;
      margin-bottom: 2px;
    }
    .header .phone {
      font-size: 11px;
      margin: 2px 0;
    }
    .invoice-info {
      margin-bottom: 8px;
      padding: 5px 0;
      border-bottom: 1px dashed #000;
      font-size: 10px;
    }
    .invoice-info div {
      margin: 2px 0;
      display: flex;
      justify-content: space-between;
    }
    .invoice-info strong {
      font-weight: bold;
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin: 5px 0;
      font-size: 10px;
    }
    th { 
      background: #000;
      color: white;
      padding: 3px 2px;
      text-align: left;
      font-size: 10px;
      text-transform: uppercase;
    }
    td { 
      padding: 3px 2px;
      border-bottom: 1px dotted #ddd;
      font-size: 10px;
    }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .totals-section {
      margin-top: 8px;
      border-top: 1px dashed #000;
      padding-top: 5px;
    }
    .totals-table {
      width: 100%;
      font-size: 10px;
    }
    .totals-table td {
      padding: 2px 0;
    }
    .totals-table .total-row {
      font-weight: bold;
      font-size: 12px;
      border-top: 1px solid #000;
      padding-top: 3px;
    }
    .footer { 
      text-align: center; 
      margin-top: 10px; 
      padding-top: 5px;
      border-top: 1px dashed #000;
      font-size: 10px; 
    }
    .payment-info {
      margin: 5px 0;
      padding: 3px;
      border: 1px dashed #000;
      font-size: 10px;
    }
    @media print { 
      body { padding: 0; }
      .no-print { display: none; }
    }
  `;

  // A4 print styling (with images)
  const a4Styles = `
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: "Segoe UI", Arial, sans-serif; 
      padding: 20px; 
      max-width: 800px; 
      margin: 0 auto;
      color: #333;
    }
    .header { 
      text-align: center; 
      margin-bottom: 30px; 
      border-bottom: 2px solid #333;
      padding-bottom: 15px;
    }
    .header h1 { 
      margin: 0; 
      font-size: 28px; 
      font-weight: bold;
      margin-bottom: 5px;
    }
    .header .phone {
      font-size: 14px;
      color: #666;
      margin: 5px 0;
    }
    .invoice-info {
      display: flex;
      justify-content: space-between;
      margin-bottom: 20px;
      padding: 15px;
      background: #f5f5f5;
      border-radius: 5px;
    }
    .invoice-info div {
      flex: 1;
    }
    .invoice-info strong {
      display: block;
      margin-bottom: 5px;
      color: #000;
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin: 20px 0;
    }
    th { 
      background: #333;
      color: white;
      padding: 12px 8px;
      text-align: left;
      font-size: 12px;
      text-transform: uppercase;
      border: 1px solid #333;
    }
    td { 
      padding: 10px 8px;
      border: 1px solid #ddd;
      font-size: 12px;
    }
    tr:nth-child(even) {
      background: #f9f9f9;
    }
    .product-cell {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .product-img {
      width: 50px;
      height: 50px;
      object-fit: cover;
      border-radius: 4px;
      border: 1px solid #ddd;
    }
    .product-info {
      flex: 1;
    }
    .product-name {
      font-weight: bold;
      margin-bottom: 3px;
    }
    .product-sku {
      color: #666;
      font-size: 11px;
    }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .totals-section {
      margin-top: 20px;
      float: right;
      width: 300px;
    }
    .totals-table {
      width: 100%;
      border: 1px solid #ddd;
    }
    .totals-table td {
      padding: 8px;
      border: 1px solid #ddd;
    }
    .totals-table .label {
      font-weight: normal;
      background: #f5f5f5;
    }
    .totals-table .total-row {
      font-weight: bold;
      font-size: 14px;
      background: #333;
      color: white;
    }
    .footer { 
      clear: both;
      text-align: center; 
      margin-top: 40px; 
      padding-top: 20px;
      border-top: 2px solid #333;
      font-size: 12px; 
      color: #666; 
    }
    .payment-info {
      margin: 20px 0;
      padding: 10px;
      background: #f0f0f0;
      border-left: 4px solid #333;
      font-size: 13px;
    }
    @media print { 
      body { padding: 10px; }
      .no-print { display: none; }
    }
  `;

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice - ${order.order_number}</title>
      <style>${isThermal ? thermalStyles : a4Styles}</style>
    </head>
    <body>
      <div class="header">
        <h1>Amorlias Mart</h1>
        <p class="phone">+919462252525</p>
      </div>
      
      <div class="invoice-info">
        ${isThermal ? `
          <div><strong>Order:</strong> <span>${order.order_number}</span></div>
          <div><strong>Date:</strong> <span>${new Date(order.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span></div>
          <div><strong>Items:</strong> <span>${order.items.reduce((sum, item) => sum + item.quantity, 0)}</span></div>
        ` : `
          <div>
            <strong>Order ID:</strong>
            <span>${order.order_number}</span>
          </div>
          <div>
            <strong>Date:</strong>
            <span>${new Date(order.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          <div>
            <strong>Total Products:</strong>
            <span>${order.items.reduce((sum, item) => sum + item.quantity, 0)}</span>
          </div>
        `}
      </div>

      <table>
        <thead>
          <tr>
            ${!isThermal ? '<th style="width: 50px;">No.</th>' : '<th style="width: 20px;">#</th>'}
            <th>Product</th>
            <th style="width: ${isThermal ? '40px' : '80px'};" class="text-center">Qty</th>
            ${!isThermal ? '<th style="width: 100px;" class="text-right">Price</th>' : ''}
            ${!isThermal && order.gst_applied ? '<th style="width: 80px;" class="text-center">Tax</th>' : ''}
            <th style="width: ${isThermal ? '60px' : '100px'};" class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          ${order.items.map((item, index) => {
            const itemSubtotal = item.price * item.quantity;
            const gstRate = item.gst_rate || 18;
            const itemTotal = item.total || (itemSubtotal + (itemSubtotal * gstRate / 100));
            const hasDiscount = (item.discount > 0 || item.takeaway_discount > 0);
            
            if (isThermal) {
              // Thermal: No images, compact layout
              return `
                <tr>
                  <td class="text-center">${index + 1}</td>
                  <td>
                    <div>${item.product_name}</div>
                    <div style="font-size: 9px; color: #666;">${item.sku || 'N/A'}</div>
                  </td>
                  <td class="text-center">${item.quantity}</td>
                  <td class="text-right">₹${itemTotal.toFixed(2)}</td>
                </tr>
              `;
            } else {
              // A4: With images
              return `
                <tr>
                  <td class="text-center">${index + 1}</td>
                  <td>
                    <div class="product-cell">
                      ${item.image_url ? `<img src="${item.image_url}" class="product-img" alt="${item.product_name}">` : ''}
                      <div class="product-info">
                        <div class="product-name">${item.product_name}</div>
                        <div class="product-sku">${item.sku || 'N/A'}</div>
                      </div>
                    </div>
                  </td>
                  <td class="text-center">${item.quantity}</td>
                  <td class="text-right">₹${item.price.toFixed(2)}</td>
                  <td class="text-center">${order.gst_applied ? `${gstRate}% GST` : '-'}</td>
                  <td class="text-right">
                    ₹${itemTotal.toFixed(2)}
                    ${hasDiscount ? `<br/><small style="color:red;font-size:10px;">(Disc: ₹${(item.discount || 0).toFixed(2)})</small>` : ''}
                  </td>
                </tr>
              `;
            }
          }).join('')}
        </tbody>
      </table>

      <div class="totals-section">
        <table class="totals-table">
          <tr>
            <td ${isThermal ? '' : 'class="label"'}>Subtotal</td>
            <td class="text-right">₹${order.subtotal.toFixed(2)}</td>
          </tr>
          ${order.gst_applied ? `
            <tr>
              <td ${isThermal ? '' : 'class="label"'}>GST (18%)</td>
              <td class="text-right">₹${order.gst_total.toFixed(2)}</td>
            </tr>
          ` : ''}
          ${order.discount_amount > 0 ? `
            <tr>
              <td ${isThermal ? '' : 'class="label"'}>Discount</td>
              <td class="text-right" style="color: #e74c3c;">-₹${order.discount_amount.toFixed(2)}</td>
            </tr>
          ` : ''}
          <tr class="total-row">
            <td>TOTAL</td>
            <td class="text-right">₹${order.grand_total.toFixed(2)}</td>
          </tr>
        </table>
      </div>

      <div class="payment-info">
        <strong>Payment:</strong> ${order.payment_method.toUpperCase()}
      </div>

      <div class="footer">
        <p><strong>Thank you for shopping with us!</strong></p>
        ${isThermal ? '<p>Visit again</p>' : '<p>Visit again at Amorlias Mart</p>'}
      </div>
      
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
          }, 500);
        };
      </script>
    </body>
    </html>
  `);
  printWindow.document.close();
};
